import { useFetch } from '@/composables/useFetch';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { ref, watch } from 'vue';
import { useEnvStore } from './envStore';
import { useUserStore } from './useUserStore';
// import { useI18n } from 'vue-i18n';
import i18n from '@/i18n';
import type { Postit, Task } from '@/types/planning';

export const usePostitsStore = defineStore('postits', () => {
  const user = ref(useUserStore().user);

  const postits = ref<Postit[]>([]);
  const selectedPostit = ref<Postit | null>(null);
  const shouldRefetch = ref<boolean>(false);
  const isLoading = ref(false);
  const currentDate = ref(new Date());
  const weekRange = ref(getWeekRange(currentDate.value));

  const loadedWindow = ref<{ dates: string[]; has_prev: boolean; has_next: boolean } | null>(null);

  function toISO(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  const { fetchData } = useFetch();
  const toast = useToast();
  const t = (key) => i18n.global.t(key);

  function getWeekRange(date: Date): { start: Date; end: Date } {
    const start = new Date(date);
    const dayOfWeek = start.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go to Monday of the same week
    start.setDate(start.getDate() + diffToMonday);

    const end = new Date(start);
    end.setDate(start.getDate() + 6); // Get the following Sunday

    return { start, end };
  }

  function selectPostit(postitId: number) {
    const postit = postits.value.find((p) => p.id === postitId);
    if (postit) {
      console.log('postit found locally:', postit);
      selectedPostit.value = postit;
    } else {
      console.log('postit not found locally, fetching from backend:', postitId);
      // Fetch from backend if not found locally
      fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}`)
        .then(({ data, error }) => {
          console.log('Fetched postit:', data);
          if (!error && data?.postit) {
            postits.value.push(data.postit);
            selectedPostit.value = data.postit;
          } else {
            selectedPostit.value = null;
          }
        })
        .catch(() => {
          selectedPostit.value = null;
        });
    }
    selectedPostit.value = postit || null;
  }

  async function addPostit(newPostit: Postit, restaurantId: string | null, router) {
    if (!restaurantId) return;

    const tempId = Date.now();
    const newPlannedPostit: Postit = {
      ...newPostit,
      tempId: tempId,
    };

    postits.value.push(newPlannedPostit);

    // Build payload according to backend validation
    const payload: {
      title: string | undefined;
      restaurant_id: string;
      planned_date: string;
      is_done?: boolean;
      tasks?: { id: number }[];
    } = {
      title: newPlannedPostit.title,
      restaurant_id: restaurantId,
      planned_date: newPlannedPostit.planned_date,
    };
    if (typeof newPlannedPostit.is_done === 'boolean') {
      payload.is_done = newPlannedPostit.is_done;
    }
    if (Array.isArray(newPlannedPostit.tasks)) {
      payload.tasks = newPlannedPostit.tasks.map((task) => ({ id: task.id }));
    }

    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/planning/postits`, 'POST', payload);
      if (error) throw error;

      const index = postits.value.findIndex((postit) => postit.tempId === tempId);
      if (index !== -1) {
        postits.value[index] = { ...newPlannedPostit, ...data.postit, tempId: null };
        router.push(`/employee/postits/${data.postit.id}`);
      }
    } catch (error) {
      console.error('Failed to add planned postit', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('postits.plannedPostit.add.error'),
        life: 1500,
      });
      postits.value = postits.value.filter((postit) => postit.tempId !== tempId);
    }
  }

  async function deletePostit(postitId: number, router) {
    const postitIndex = postits.value.findIndex((postit) => postit.id === postitId);
    if (postitIndex === -1) return;
    const postitToDelete = { ...postits.value[postitIndex] };
    postits.value.splice(postitIndex, 1);

    try {
      const { error } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}`, 'DELETE');
      if (error) throw error;
      if (selectedPostit.value && selectedPostit.value.id === postitId) {
        selectedPostit.value = null;
      }
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('employee.postits.delete.success'),
        life: 1500,
      });
      router.push({ path: '/postits' });
    } catch (error) {
      console.error('Failed to delete planned postit', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.postits.delete.error'),
        life: 1500,
      });
      postits.value.splice(postitIndex, 0, postitToDelete);
    }
  }

  async function fetchPostitsByDateRange(startDate: string, endDate: string, restaurantId: string | null) {
    isLoading.value = true;
    try {
      const url = `${useEnvStore().apiUrl}/planning/postits?restaurant_id=${restaurantId}&start_date=${startDate}&end_date=${endDate}`;
      const { data, error } = await fetchData(url, 'GET');
      console.log('Fetched postits:', data);
      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('employee.postits.fetchByDateRange.error'),
          life: 1500,
        });
        throw new Error('Failed to fetch postits by date range');
      }
      const fetchedPostits = data.postits as Postit[];

      fetchedPostits.forEach((postit) => {
        postit.planned_date = new Date(postit.planned_date).toISOString().split('T')[0];
      });

      const existingPostits = postits.value;
      fetchedPostits.forEach((fetchedPostit) => {
        const index = existingPostits.findIndex((postit) => postit.id === fetchedPostit.id);
        if (index !== -1) {
          existingPostits[index] = fetchedPostit;
        } else {
          existingPostits.push(fetchedPostit);
        }
      });

      postits.value = [...existingPostits];
    } catch (error) {
      console.error('Failed to fetch postits by date range', error);
    } finally {
      isLoading.value = false;
    }
  }

  function getPostitsByDate(date: string): Postit[] {
    const targetDate = new Date(date).toISOString().split('T')[0];
    return postits.value.filter((postit) => {
      const postitDate = new Date(postit.planned_date).toISOString().split('T')[0];
      return postitDate === targetDate;
    });
  }

  async function fetchPostitsIfNeeded(startDate: string, endDate: string, restaurantId: string | null) {
    if (!restaurantId) return;
    const isDataLoaded = postits.value.some(
      (postit) => new Date(postit.planned_date) >= new Date(startDate) && new Date(postit.planned_date) <= new Date(endDate),
    );
    if (!isDataLoaded || shouldRefetch.value) {
      await fetchPostitsByDateRange(startDate, endDate, restaurantId);
      shouldRefetch.value = false;
    }
  }

  async function assignUsersToPostit(postitId: number, restaurantUserIds: number[]) {
    const { data: _data, error } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}/assign-users`, 'POST', {
      restaurant_user_ids: restaurantUserIds,
    });
    if (error) {
      throw error;
    }
    // Update local postit
    if (selectedPostit.value && selectedPostit.value.id === postitId) {
      selectedPostit.value.restaurant_user_ids = restaurantUserIds;
    }
  }

  async function detachUsersFromPostit(postitId: number, restaurantUserIds: number[]) {
    const { data: _data, error } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}/detach-users`, 'POST', {
      restaurant_user_ids: restaurantUserIds,
    });
    if (error) {
      throw error;
    }
    // Update local postit
    if (selectedPostit.value && selectedPostit.value.id === postitId) {
      selectedPostit.value.restaurant_user_ids = [];
    }
  }

  // watch(shouldRefetch, async (newValue) => {
  //   if (newValue) {
  //     postits.value.sort((a, b) => a.date.localeCompare(b.date));
  //     const restaurantId = user.value?.restaurants[0]?.id || null;
  //     const startDate = postits.value[0].date;
  //     const endDate = postits.value[postits.value.length - 1].date;
  //     postits.value = [];
  //     await fetchPostitsByDateRange(startDate, endDate, restaurantId);
  //     shouldRefetch.value = false;
  //   }
  // });

  watch(
    weekRange,
    async (newRange) => {
      // console.log("Change planning Store.weekrange")

      const restaurantId = user.value?.restaurants[0]?.id || null;
      await fetchPostitsIfNeeded(newRange.start.toISOString().split('T')[0], newRange.end.toISOString().split('T')[0], restaurantId);
    },
    { deep: true, immediate: true },
  );

  async function createTask(postitId: number, task: Task) {
    const tempId = Date.now();
    const optimisticTask = { ...task, id: tempId, tempId };
    // Also update selectedPostit if it matches
    if (selectedPostit.value && selectedPostit.value.id === postitId) {
      if (!selectedPostit.value.tasks) selectedPostit.value.tasks = [];
      selectedPostit.value.tasks.push(optimisticTask);
    }

    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}/tasks`, 'POST', { ...task });
      if (error) throw error;

      // Replace the optimistic task with the real one from backend
      if (data?.postit) {
        // Update postit in store
        const idx = postits.value.findIndex((p) => p.id === postitId);
        if (idx !== -1) postits.value[idx] = data.postit;
        // Update selectedPostit if needed
        if (selectedPostit.value && selectedPostit.value.id === postitId) {
          selectedPostit.value = data.postit;
        }
      }
    } catch (error) {
      if (selectedPostit.value && selectedPostit.value.id === postitId && selectedPostit.value.tasks) {
        selectedPostit.value.tasks = selectedPostit.value.tasks.filter((t) => t.id !== tempId);
      }
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.postits.task.create.error'),
        life: 1500,
      });
      throw error;
    }
  }

  async function createTasks(postitId: number, tasks: Task[]) {
    const { data, error } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}/tasks/bulk`, 'POST', { tasks });
    if (error) throw error;
    // Update local postit from backend response if provided
    if (data?.postit) {
      const idx = postits.value.findIndex((p) => p.id === postitId);
      if (idx !== -1) postits.value[idx] = data.postit;
      if (selectedPostit.value && selectedPostit.value.id === postitId) {
        selectedPostit.value = data.postit;
      }
    }
  }

  async function deleteTask(postitId: number, taskId: number) {
    // Optimistically remove the task
    const postit = postits.value.find((p) => p.id === postitId);
    if (postit && postit.tasks) {
      postit.tasks = postit.tasks.filter((t) => t.id !== taskId);
    }
    if (selectedPostit.value && selectedPostit.value.id === postitId && selectedPostit.value.tasks) {
      selectedPostit.value.tasks = selectedPostit.value.tasks.filter((t) => t.id !== taskId);
    }
    try {
      const { error, data } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}/tasks/${taskId}`, 'DELETE');
      if (error) throw error;
      // Optionally update postit from backend response if provided
      if (data?.postit) {
        const idx = postits.value.findIndex((p) => p.id === postitId);
        if (idx !== -1) postits.value[idx] = data.postit;
        if (selectedPostit.value && selectedPostit.value.id === postitId) {
          selectedPostit.value = data.postit;
        }
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('postits.task.delete.error'),
        life: 1500,
      });
      throw error;
    }
  }

  async function setTaskDone(postitId: number, taskId: number, isDone: boolean) {
    // Optimistically update
    const postit = postits.value.find((p) => p.id === postitId);
    const task = postit?.tasks?.find((t) => t.id === taskId);
    if (task) task.is_done = isDone;
    if (selectedPostit.value && selectedPostit.value.id === postitId) {
      const selTask = selectedPostit.value.tasks?.find((t) => t.id === taskId);
      if (selTask) selTask.is_done = isDone;
    }
    try {
      const { error, data } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}/tasks/${taskId}/check`, 'PATCH', {
        is_done: isDone,
      });
      if (error) throw error;
      // Optionally update postit from backend response if provided
      if (data?.postit) {
        const idx = postits.value.findIndex((p) => p.id === postitId);
        if (idx !== -1) postits.value[idx] = data.postit;
        if (selectedPostit.value && selectedPostit.value.id === postitId) {
          selectedPostit.value = data.postit;
        }
      }
    } catch (error) {
      console.error('Failed to set task done', error);
      // Revert on error
      if (task) task.is_done = !isDone;
      if (selectedPostit.value && selectedPostit.value.id === postitId) {
        const selTask = selectedPostit.value.tasks?.find((t) => t.id === taskId);
        if (selTask) selTask.is_done = !isDone;
      }
      throw error;
    }
  }

  async function fetchInitialWindow(restaurantId: string, days = 2) {
    const today = toISO(new Date());
    await fetchAndMergeWindow(today, 'next', days, restaurantId);
  }

  async function fetchAndMergeWindow(from_date: string, direction: 'next' | 'prev', days: number, restaurantId: string) {
    const url = `${useEnvStore().apiUrl}/planning/postits?restaurant_id=${restaurantId}&from_date=${from_date}&direction=${direction}&days=${days}`;
    const { data, error } = await fetchData(url, 'GET');
    if (error) throw error;

    const fetchedPostits = data.postits as Postit[];
    // Merge postits (update or add)
    fetchedPostits.forEach((postit) => {
      postit.planned_date = toISO(new Date(postit.planned_date));
      const idx = postits.value.findIndex((p) => p.id === postit.id);
      if (idx !== -1) postits.value[idx] = postit;
      else postits.value.push(postit);
    });

    // Merge/extend loadedWindow.dates
    if (!loadedWindow.value) {
      loadedWindow.value = {
        dates: data.dates,
        has_prev: data.has_prev,
        has_next: data.has_next,
      };
    } else {
      // Merge dates, keeping them sorted and unique
      const mergedDates = Array.from(new Set([...loadedWindow.value.dates, ...data.dates])).sort();
      loadedWindow.value = {
        dates: mergedDates,
        has_prev: data.has_prev,
        has_next: data.has_next,
      };
    }
  }

  async function loadPreviousDays(days = 1) {
    if (!loadedWindow.value || !user.value?.restaurants?.[0]?.id) return;
    const restaurantId = user.value.restaurants[0].id;
    const earliestDate = loadedWindow.value.dates[0];
    await fetchAndMergeWindow(earliestDate, 'prev', days, restaurantId);
  }

  async function loadNextDays(days = 2) {
    if (!loadedWindow.value || !user.value?.restaurants?.[0]?.id) return;
    const restaurantId = user.value.restaurants[0].id;
    const latestDate = loadedWindow.value.dates[loadedWindow.value.dates.length - 1];
    await fetchAndMergeWindow(latestDate, 'next', days, restaurantId);
  }

  async function updatePostit(postitId: number, updates: { planned_date?: string; title?: string; is_done?: boolean }) {
    try {
      // Only send fields that are present in updates
      const payload: { planned_date?: string; title?: string; is_done?: boolean } = {};
      if (updates.planned_date !== undefined) payload.planned_date = updates.planned_date;
      if (updates.title !== undefined) payload.title = updates.title;
      if (updates.is_done !== undefined) payload.is_done = updates.is_done;

      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/planning/postits/${postitId}`, 'PATCH', payload);
      if (error) throw error;

      // Update local postit in store
      if (data?.postit) {
        const idx = postits.value.findIndex((p) => p.id === postitId);
        if (idx !== -1) postits.value[idx] = data.postit;
        if (selectedPostit.value && selectedPostit.value.id === postitId) {
          selectedPostit.value = data.postit;
        }
      }
      // toast.add({ severity: 'success', summary: t('common.success'), detail: t('postits.postit.update.success'), life: 1500 });
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('postits.postit.update.error'),
        life: 1500,
      });
      throw error;
    }
  }

  return {
    postits,
    selectedPostit,
    isLoading,
    shouldRefetch,
    selectPostit,
    addPostit,
    fetchPostitsByDateRange,
    getPostitsByDate,
    createTask,
    fetchPostitsIfNeeded,
    deleteTask,
    assignUsersToPostit,
    detachUsersFromPostit,
    setTaskDone,
    fetchInitialWindow,
    loadPreviousDays,
    loadNextDays,
    loadedWindow,
    updatePostit,
    createTasks,
    deletePostit,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePostitsStore, import.meta.hot));
}
